/* DocEditorDrawer — super-admin editor for Help Center topics.
 * Supports manual editing of every body field, one-click AI drafting via
 * Claude (Emergent LLM Key), embedding Loom/YouTube/Vimeo videos, and
 * attaching downloads either by external URL (Google Drive, etc.) or by
 * uploading a PDF/Doc/Image into NHF storage.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  X, Loader2, Save, Sparkles, Plus, Trash2, Link2, Upload, Video, FileText,
  AlertTriangle,
} from 'lucide-react';
import QuizEditor from './QuizEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMBED_TYPES = [
  { value: 'youtube', label: 'YouTube' },
  { value: 'loom', label: 'Loom' },
  { value: 'vimeo', label: 'Vimeo' },
  { value: 'image', label: 'Image' },
];

const DocEditorDrawer = ({ open, docId, onClose, onSaved }) => {
  const [doc, setDoc] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extraContext, setExtraContext] = useState('');
  const [error, setError] = useState('');
  const [aiNote, setAiNote] = useState('');

  useEffect(() => {
    if (!open || !docId) return;
    const load = async () => {
      setLoading(true); setError(''); setAiNote('');
      try {
        const r = await axios.get(`${BACKEND_URL}/api/admin/docs/${docId}`,
          { withCredentials: true });
        setDoc(r.data);
        setForm({
          title: r.data.title || '',
          what_it_does: r.data.what_it_does || '',
          when_to_use: r.data.when_to_use || '',
          what_if_not: r.data.what_if_not || '',
          who_should_use: r.data.who_should_use || '',
          nhf_example: r.data.nhf_example || '',
          steps: r.data.steps || [],
          common_mistakes: r.data.common_mistakes || [],
          best_practices: r.data.best_practices || [],
          glossary: r.data.glossary || [],
          media_embeds: r.data.media_embeds || [],
          downloads: r.data.downloads || [],
          related_route: r.data.related_route || '',
          objectives: r.data.objectives || [],
          estimated_minutes: r.data.estimated_minutes || '',
          difficulty: r.data.difficulty || 'Intermediate',
        });
      } catch (e) { setError(e.response?.data?.detail || 'Could not load topic.'); }
      setLoading(false);
    };
    load();
  }, [open, docId]);

  const setListItem = (key, idx, value) => {
    const next = [...(form[key] || [])];
    next[idx] = value;
    setForm({ ...form, [key]: next });
  };
  const addListItem = (key, blank = '') => setForm({ ...form, [key]: [...(form[key] || []), blank] });
  const removeListItem = (key, idx) => setForm({
    ...form, [key]: (form[key] || []).filter((_, j) => j !== idx),
  });

  const runAiDraft = async () => {
    if (!doc) return;
    setDrafting(true); setError(''); setAiNote('');
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/docs/${doc.id}/ai-draft`,
        { extra_context: extraContext || null }, { withCredentials: true });
      const d = r.data?.draft || {};
      setForm((f) => ({
        ...f,
        what_it_does: d.what_it_does || f.what_it_does,
        when_to_use: d.when_to_use || f.when_to_use,
        what_if_not: d.what_if_not || f.what_if_not,
        who_should_use: d.who_should_use || f.who_should_use,
        nhf_example: d.nhf_example || f.nhf_example,
        steps: Array.isArray(d.steps) && d.steps.length ? d.steps : f.steps,
        common_mistakes: Array.isArray(d.common_mistakes) && d.common_mistakes.length
          ? d.common_mistakes : f.common_mistakes,
        best_practices: Array.isArray(d.best_practices) && d.best_practices.length
          ? d.best_practices : f.best_practices,
        glossary: Array.isArray(d.glossary) ? d.glossary : f.glossary,
        objectives: Array.isArray(d.objectives) && d.objectives.length
          ? d.objectives : f.objectives,
        estimated_minutes: Number.isFinite(d.estimated_minutes)
          ? d.estimated_minutes : f.estimated_minutes,
      }));
      setAiNote('AI draft applied. Review the fields below and click Save to publish.');
    } catch (e) { setError(e.response?.data?.detail || 'AI draft failed.'); }
    setDrafting(false);
  };

  const uploadPdf = async (file) => {
    if (!file) return;
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${BACKEND_URL}/api/admin/docs/upload`, fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      const url = r.data?.url;
      const label = r.data?.original_name || file.name;
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      const isImage = ['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext);
      if (isImage) {
        setForm((f) => ({
          ...f,
          media_embeds: [...(f.media_embeds || []),
            { type: 'image', url, title: label, description: '' }],
        }));
      } else {
        setForm((f) => ({
          ...f,
          downloads: [...(f.downloads || []), { label, url, type: ext || 'pdf' }],
        }));
      }
    } catch (e) { setError(e.response?.data?.detail || 'Upload failed.'); }
    setUploading(false);
  };

  const save = async () => {
    if (!doc) return;
    setSaving(true); setError('');
    try {
      // Clean steps & lists (drop blanks)
      const payload = {
        title: form.title,
        what_it_does: form.what_it_does,
        when_to_use: form.when_to_use,
        what_if_not: form.what_if_not,
        who_should_use: form.who_should_use,
        nhf_example: form.nhf_example,
        related_route: form.related_route,
        objectives: (form.objectives || []).map((s) => (s || '').trim()).filter(Boolean),
        estimated_minutes: form.estimated_minutes === '' || form.estimated_minutes == null
          ? null : Number(form.estimated_minutes),
        difficulty: form.difficulty || 'Intermediate',
        steps: (form.steps || []).map((s) => (s || '').trim()).filter(Boolean),
        common_mistakes: (form.common_mistakes || []).map((s) => (s || '').trim()).filter(Boolean),
        best_practices: (form.best_practices || []).map((s) => (s || '').trim()).filter(Boolean),
        glossary: (form.glossary || []).filter((g) => g && (g.term || '').trim()),
        media_embeds: (form.media_embeds || []).filter((m) => m && (m.url || '').trim()),
        downloads: (form.downloads || []).filter((d) => d && (d.url || '').trim()),
      };
      await axios.put(`${BACKEND_URL}/api/admin/docs/${doc.id}`, payload,
        { withCredentials: true });
      onSaved?.();
      onClose?.();
    } catch (e) { setError(e.response?.data?.detail || 'Save failed.'); }
    setSaving(false);
  };

  const isSuperAdminError = useMemo(() => /super|admin|permission/i.test(error || ''), [error]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
      data-testid="doc-editor-drawer">
      <div className="bg-white w-full max-w-3xl h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-[#E5E7EB] z-10 px-4 py-3 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF]">Edit Help Topic</p>
            <h2 className="font-heading text-lg font-bold text-[#0A0A0A]">{form.title || doc?.title || 'Loading…'}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={save} disabled={saving || loading}
              className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded inline-flex items-center gap-1.5 disabled:opacity-60"
              data-testid="doc-editor-save">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Save changes
            </button>
            <button onClick={onClose} className="text-[#4B5563] hover:text-[#0A0A0A]"
              data-testid="doc-editor-close"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div>
        ) : (
          <div className="p-4 space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2.5 rounded inline-flex items-start gap-2"
                data-testid="doc-editor-error">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <div>
                  <p>{error}</p>
                  {isSuperAdminError && (
                    <p className="text-[10px] mt-0.5 text-red-600">
                      Editing requires the <code>settings:admin</code> permission (super admin).
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* AI Draft */}
            <div className="bg-[#FFF8F8] border border-[#BA1826]/30 rounded-lg p-3" data-testid="doc-ai-card">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-[#BA1826] inline-flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" /> AI-draft a richer version with Claude
                  </p>
                  <p className="text-[11px] text-[#4B5563] mt-1">
                    Pipes the current scaffold through Claude Sonnet 4.5 (Emergent LLM Key) and fills in the
                    body fields below. Always review before saving — nothing is auto-published.
                  </p>
                </div>
                <button onClick={runAiDraft} disabled={drafting}
                  className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded inline-flex items-center gap-1.5 disabled:opacity-60"
                  data-testid="doc-ai-draft-btn">
                  {drafting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  {drafting ? 'Drafting…' : 'AI draft'}
                </button>
              </div>
              <input value={extraContext} onChange={(e) => setExtraContext(e.target.value)}
                placeholder="Optional: extra context for Claude (audience, recent changes, tone…)"
                className="mt-2 w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-xs"
                data-testid="doc-ai-extra-context" />
              {aiNote && <p className="text-[11px] text-emerald-700 mt-1.5" data-testid="doc-ai-note">{aiNote}</p>}
            </div>

            {/* Body fields */}
            <Field label="Title" value={form.title}
              onChange={(v) => setForm({ ...form, title: v })} testid="doc-edit-title" />
            <Field label="Related route (optional)" value={form.related_route}
              onChange={(v) => setForm({ ...form, related_route: v })}
              placeholder="/admin/finance" testid="doc-edit-route" />

            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <StringListEditor title="What you'll learn (objectives)"
                  list={form.objectives}
                  onAdd={() => addListItem('objectives', '')}
                  onChange={(i, v) => setListItem('objectives', i, v)}
                  onRemove={(i) => removeListItem('objectives', i)}
                  testid="doc-edit-objectives" />
              </div>
              <div className="space-y-2">
                <div>
                  <label className="block text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF] mb-1">
                    Difficulty
                  </label>
                  <select value={form.difficulty || 'Intermediate'}
                    onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
                    className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-xs bg-white"
                    data-testid="doc-edit-difficulty">
                    <option value="Beginner">Beginner</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF] mb-1">
                    Estimated minutes
                  </label>
                  <input type="number" min="1" max="240"
                    value={form.estimated_minutes ?? ''}
                    onChange={(e) => setForm({ ...form, estimated_minutes: e.target.value })}
                    placeholder="(auto)"
                    className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-xs"
                    data-testid="doc-edit-estimated-minutes" />
                  <p className="text-[10px] text-[#9CA3AF] mt-1">
                    Leave blank to auto-derive.
                  </p>
                </div>
              </div>
            </div>
            <Field multi label="What does this do?" value={form.what_it_does}
              onChange={(v) => setForm({ ...form, what_it_does: v })} testid="doc-edit-what" />
            <Field multi label="When should I use it?" value={form.when_to_use}
              onChange={(v) => setForm({ ...form, when_to_use: v })} testid="doc-edit-when" />
            <Field multi label="What happens if I don't use it correctly?" value={form.what_if_not}
              onChange={(v) => setForm({ ...form, what_if_not: v })} testid="doc-edit-ifnot" />
            <Field multi label="Who should use this" value={form.who_should_use}
              onChange={(v) => setForm({ ...form, who_should_use: v })} testid="doc-edit-who" />
            <Field multi label="Real-world NHF example" value={form.nhf_example}
              onChange={(v) => setForm({ ...form, nhf_example: v })} testid="doc-edit-example" />

            <StringListEditor title="Step-by-step" list={form.steps}
              onAdd={() => addListItem('steps', '')}
              onChange={(i, v) => setListItem('steps', i, v)}
              onRemove={(i) => removeListItem('steps', i)}
              testid="doc-edit-steps" />
            <StringListEditor title="Common mistakes" list={form.common_mistakes}
              onAdd={() => addListItem('common_mistakes', '')}
              onChange={(i, v) => setListItem('common_mistakes', i, v)}
              onRemove={(i) => removeListItem('common_mistakes', i)}
              testid="doc-edit-mistakes" />
            <StringListEditor title="Best practices" list={form.best_practices}
              onAdd={() => addListItem('best_practices', '')}
              onChange={(i, v) => setListItem('best_practices', i, v)}
              onRemove={(i) => removeListItem('best_practices', i)}
              testid="doc-edit-best" />

            {/* Media embeds */}
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-3" data-testid="doc-embeds-card">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold uppercase tracking-wider text-[#4B5563] inline-flex items-center gap-1.5">
                  <Video className="w-3.5 h-3.5 text-[#BA1826]" /> Video walkthroughs & images
                </p>
                <button onClick={() => addListItem('media_embeds',
                    { type: 'youtube', url: '', title: '', description: '' })}
                  className="text-[10px] px-2 py-1 border border-[#E5E7EB] rounded inline-flex items-center gap-1"
                  data-testid="doc-embed-add">
                  <Plus className="w-3 h-3" /> Add embed
                </button>
              </div>
              {(form.media_embeds || []).length === 0 && (
                <p className="text-[11px] italic text-[#9CA3AF]">
                  No embeds yet. Paste a YouTube, Loom, or Vimeo URL to embed an explanatory walkthrough.
                </p>
              )}
              {(form.media_embeds || []).map((m, i) => (
                <div key={i} className="grid grid-cols-12 gap-1.5 mb-1.5 items-center"
                  data-testid={`doc-embed-row-${i}`}>
                  <select value={m.type || 'youtube'}
                    onChange={(e) => setListItem('media_embeds', i, { ...m, type: e.target.value })}
                    className="col-span-2 px-2 py-1 border border-[#E5E7EB] rounded text-xs">
                    {EMBED_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <input value={m.url || ''}
                    onChange={(e) => setListItem('media_embeds', i, { ...m, url: e.target.value })}
                    placeholder="https://www.youtube.com/watch?v=… or https://www.loom.com/share/…"
                    className="col-span-5 px-2 py-1 border border-[#E5E7EB] rounded text-xs" />
                  <input value={m.title || ''}
                    onChange={(e) => setListItem('media_embeds', i, { ...m, title: e.target.value })}
                    placeholder="Title"
                    className="col-span-4 px-2 py-1 border border-[#E5E7EB] rounded text-xs" />
                  <button onClick={() => removeListItem('media_embeds', i)}
                    className="col-span-1 text-[#BA1826] flex justify-center"
                    data-testid={`doc-embed-remove-${i}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
            </div>

            {/* Downloads — URL or upload */}
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-3" data-testid="doc-downloads-card">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <p className="text-xs font-bold uppercase tracking-wider text-[#4B5563] inline-flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-[#BA1826]" /> Downloads (PDFs, slide decks, policies)
                </p>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => addListItem('downloads',
                      { label: '', url: '', type: 'pdf' })}
                    className="text-[10px] px-2 py-1 border border-[#E5E7EB] rounded inline-flex items-center gap-1"
                    data-testid="doc-download-add-url">
                    <Link2 className="w-3 h-3" /> Add URL
                  </button>
                  <label className="text-[10px] px-2 py-1 bg-[#0A0A0A] text-white rounded inline-flex items-center gap-1 cursor-pointer"
                    data-testid="doc-download-upload-label">
                    {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                    Upload file
                    <input type="file" className="hidden" disabled={uploading}
                      onChange={(e) => uploadPdf(e.target.files?.[0])}
                      accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.docx"
                      data-testid="doc-download-upload-input" />
                  </label>
                </div>
              </div>
              <p className="text-[10px] text-[#9CA3AF] mb-2">
                Uploaded files are stored in NHF storage and served from <code>/api/uploads/…</code>. External URLs
                (Google Drive, Dropbox) are also supported — keep links public-readable if board members need them.
              </p>
              {(form.downloads || []).length === 0 && (
                <p className="text-[11px] italic text-[#9CA3AF]">No downloads attached yet.</p>
              )}
              {(form.downloads || []).map((d, i) => (
                <div key={i} className="grid grid-cols-12 gap-1.5 mb-1.5 items-center"
                  data-testid={`doc-download-row-${i}`}>
                  <input value={d.label || ''}
                    onChange={(e) => setListItem('downloads', i, { ...d, label: e.target.value })}
                    placeholder="Label (e.g. 2026 Board Onboarding Packet)"
                    className="col-span-4 px-2 py-1 border border-[#E5E7EB] rounded text-xs" />
                  <input value={d.url || ''}
                    onChange={(e) => setListItem('downloads', i, { ...d, url: e.target.value })}
                    placeholder="https://… or /api/uploads/<id>"
                    className="col-span-6 px-2 py-1 border border-[#E5E7EB] rounded text-xs font-mono" />
                  <input value={d.type || 'pdf'}
                    onChange={(e) => setListItem('downloads', i, { ...d, type: e.target.value })}
                    placeholder="pdf"
                    className="col-span-1 px-2 py-1 border border-[#E5E7EB] rounded text-xs" />
                  <button onClick={() => removeListItem('downloads', i)}
                    className="col-span-1 text-[#BA1826] flex justify-center"
                    data-testid={`doc-download-remove-${i}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
            </div>

            {/* Quiz / Knowledge Check */}
            <QuizEditor docId={doc?.id} />

            {/* Glossary */}
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-3" data-testid="doc-glossary-card">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold uppercase tracking-wider text-[#4B5563]">Glossary</p>
                <button onClick={() => addListItem('glossary', { term: '', definition: '' })}
                  className="text-[10px] px-2 py-1 border border-[#E5E7EB] rounded inline-flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Add term
                </button>
              </div>
              {(form.glossary || []).map((g, i) => (
                <div key={i} className="grid grid-cols-12 gap-1.5 mb-1.5">
                  <input value={g.term || ''}
                    onChange={(e) => setListItem('glossary', i, { ...g, term: e.target.value })}
                    placeholder="Term" className="col-span-3 px-2 py-1 border border-[#E5E7EB] rounded text-xs" />
                  <input value={g.definition || ''}
                    onChange={(e) => setListItem('glossary', i, { ...g, definition: e.target.value })}
                    placeholder="Definition" className="col-span-8 px-2 py-1 border border-[#E5E7EB] rounded text-xs" />
                  <button onClick={() => removeListItem('glossary', i)}
                    className="col-span-1 text-[#BA1826] flex justify-center"><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, multi, placeholder, testid }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF] mb-1">{label}</label>
    {multi ? (
      <textarea value={value || ''} onChange={(e) => onChange(e.target.value)}
        rows={3} placeholder={placeholder}
        className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-xs"
        data-testid={testid} />
    ) : (
      <input value={value || ''} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-xs"
        data-testid={testid} />
    )}
  </div>
);

const StringListEditor = ({ title, list, onAdd, onChange, onRemove, testid }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-lg p-3" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <p className="text-xs font-bold uppercase tracking-wider text-[#4B5563]">{title}</p>
      <button onClick={onAdd}
        className="text-[10px] px-2 py-1 border border-[#E5E7EB] rounded inline-flex items-center gap-1"
        data-testid={`${testid}-add`}>
        <Plus className="w-3 h-3" /> Add
      </button>
    </div>
    {(list || []).length === 0 && (
      <p className="text-[11px] italic text-[#9CA3AF]">Empty.</p>
    )}
    {(list || []).map((item, i) => (
      <div key={i} className="flex items-start gap-1.5 mb-1.5">
        <span className="text-[10px] font-mono text-[#9CA3AF] mt-1.5 w-6 text-right">{i + 1}.</span>
        <textarea value={item || ''} onChange={(e) => onChange(i, e.target.value)}
          rows={1} className="flex-1 px-2 py-1 border border-[#E5E7EB] rounded text-xs"
          data-testid={`${testid}-row-${i}`} />
        <button onClick={() => onRemove(i)}
          className="text-[#BA1826] mt-1.5"
          data-testid={`${testid}-remove-${i}`}><Trash2 className="w-3 h-3" /></button>
      </div>
    ))}
  </div>
);

export default DocEditorDrawer;
